import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Quote } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// AJ's actual images
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_a68a521f-d588-4c7a-99ce-4eb4c9c2d03b/artifacts/blvr559f_IMG_3348.jpeg";
const GROUP_IMAGE = "https://customer-assets.emergentagent.com/job_a68a521f-d588-4c7a-99ce-4eb4c9c2d03b/artifacts/s50xz4s0_c681515e-c9a8-48a3-8db6-282e392fd3df.jpeg";

const Results = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    setIsVisible(true);
  }, [setIsVisible]);

  useEffect(() => {
    fetch(`${API_URL}/api/content/testimonials`)
      .then(res => res.json())
      .then(setTestimonials)
      .catch(() => {});
  }, []);

  const stats = [
    { value: '5+', label: 'Years Coaching' },
    { value: '100+', label: 'Clients Trained' },
    { value: '95%', label: 'Client Retention' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-end">
        <div className="absolute inset-0 z-0">
          <img 
            src={GROUP_IMAGE}
            alt="Heft Training clients at HYROX"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        </div>
        <div className={`container relative z-10 pb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h1 className="heading-display text-white">Results</h1>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16">
        <div className="container">
          <div className="grid md:grid-cols-3 gap-8 max-w-3xl mx-auto text-center">
            {stats.map((stat) => (
              <div key={stat.label}>
                <div className="text-5xl font-serif text-[#C9A962] mb-2">{stat.value}</div>
                <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#6B6B6B]">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-[#F5F0EB] py-20 md:py-28">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-large mb-6">What Results Look Like</h2>
            <p className="body-large">
              Success isn't just about how you look — it's about how you feel. My clients 
              report increased confidence, better energy levels, and sustainable habits that 
              last beyond our sessions together.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <h2 className="heading-large text-center mb-16">What Clients Say</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="border border-[#E5E0DB] p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={`star-${i}`} size={14} fill="#C9A962" className="text-[#C9A962]" />
                  ))}
                </div>
                <Quote size={32} className="text-[#E5E0DB] mb-4" />
                <p className="font-serif text-lg italic text-[#2D2D2D] mb-6">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center justify-between border-t border-[#E5E0DB] pt-4">
                  <div>
                    <p className="font-semibold text-sm text-[#1A1A1A]">{testimonial.name}</p>
                    <p className="text-xs text-[#6B6B6B]">{testimonial.role}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold tracking-[0.1em] uppercase text-[#C9A962]">
                      {testimonial.duration}
                    </span>
                    <p className="text-xs text-[#6B6B6B] mt-1">{testimonial.result}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* More Than Numbers */}
      <section className="bg-[#EDE8E3] py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <img 
                src={HERO_IMAGE}
                alt="AJ running Oxford Half Marathon"
                className="w-full aspect-[4/3] object-cover object-top"
              />
            </div>
            <div>
              <h2 className="heading-large mb-6">More Than Numbers</h2>
              <p className="body-large mb-6">
                I don't focus on before/after photos or dramatic transformations. 
                Real progress is about feeling strong, moving well, and building 
                habits that serve you for life.
              </p>
              <p className="body-regular text-[#6B6B6B] mb-8">
                My clients range from complete beginners to experienced gym-goers, 
                but they all share something in common: they were ready to commit 
                to a realistic, sustainable approach to fitness.
              </p>
              <Link to="/about" className="btn-outline">
                Learn About My Approach
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1A1A1A] py-20 md:py-28">
        <div className="container text-center">
          <h2 className="heading-large text-white mb-6">Start Your Success Story</h2>
          <p className="body-large text-gray-400 mb-10 max-w-xl mx-auto">
            Ready to build your own sustainable fitness journey? Let's talk about your goals.
          </p>
          <Link to="/contact" className="btn-gold inline-flex items-center gap-2">
            Book Free Consultation
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Results;
